import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { SupportedLanguage } from '@aidkitorg/types/lib/legacy/airtable';
import InterfaceContext, { ConfigurationContext, PublicConfigurationContext } from './Context';
import { useModularMarkdown } from './Hooks/ModularMarkdown';
import { useLocalizedStrings } from './Localization';
import { usePost } from './API';
import { Button, Input, InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, MonthDayYearInput } from '@aidkitorg/component-library';
import { LanguageDropdown } from './Components/LanguageDropdown';
import { PortalHeader } from './Components/PortalHeader';
import { useContactValidation } from './utils/contactValidation';
import { trySetPortalToken } from './Hooks/PortalSession';

type Step = 'fields' | 'code';

export function ApplicantPortalLoginPage() {
  const context = useContext(InterfaceContext);
  const configuration = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const L = useLocalizedStrings();
  const history = useHistory();
  const location = useLocation();
  const [, setCookie] = useCookies(['portal']);
  const step: Step = new URLSearchParams(location.search).get('step') === 'code' ? 'code' : 'fields';

  const portal = publicConfig.applicantPortal;
  const loginFieldKeys = portal?.loginFieldKeys ?? [];
  const requiresBirthDate = loginFieldKeys.includes('birth_date');
  const description = portal?.loginDescription;
  const defaultDescription = requiresBirthDate
    ? L.auth.default_login_description_with_birth_date
    : L.auth.default_login_description;
  const userDescriptionContent = description?.[context.lang as SupportedLanguage] || description?.en;
  const descriptionMarkdown = userDescriptionContent || defaultDescription;
  const isUsingDefaultDescription = !userDescriptionContent;
  const renderedDescription = useModularMarkdown({ content: descriptionMarkdown, info: {} });
  const disclaimer = portal?.loginDisclaimer;
  const userDisclaimerContent = disclaimer?.[context.lang as SupportedLanguage] || disclaimer?.en;
  const disclaimerMarkdown = userDisclaimerContent || L.auth.default_login_disclaimer;
  const isUsingDefaultDisclaimer = !userDisclaimerContent;
  const renderedDisclaimer = useModularMarkdown({ content: disclaimerMarkdown, info: {} });
  const troubleshootHelper = useModularMarkdown({ content: L.auth.code_sent_to_email_troubleshoot, info: {} });

  const {
    contact: email,
    contactValidationError: emailError,
    contactOnChange: setEmail,
  } = useContactValidation({
    contactType: 'email',
    messages: {
      invalidEmail: L.questions.textentry.must_be_a_valid_email,
      invalidPhone: L.questions.textentry.please_enter_valid_us_number,
      invalidPhoneOrEmail: L.questions.textentry.must_be_a_valid_phone_or_email,
    },
  });
  const [birthDate, setBirthDate] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const attestation = useRef('');

  useEffect(() => {
    if (step === 'fields') {
      setCode('');
      setChallenge('');
      setError('');
    }
  }, [step]);

  useEffect(() => {
    if (step === 'code' && !challenge) {
      history.replace(location.pathname);
    }
  }, [step, challenge, history, location.pathname]);

  const sendConfirmation = usePost('/confirm/phone');
  const checkCode = usePost('/confirm/phone_code');
  const getApplicantCandidates = usePost('/applicant/get_candidates', {
    token: () => attestation.current,
  });

  const onSubmitFields = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    if (!email || emailError) return;
    setSubmitting(true);
    try {
      const result = await sendConfirmation({ phone: email, language: context.lang });
      if (result.error || !result.token) {
        setError(result.error || L.questions.contact_confirmation.send_confirmation_code_failed);
        return;
      }
      setChallenge(result.token);
      history.push(`${location.pathname}?step=code`);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    if (code.length !== 6) {
      setError(L.questions.contact_confirmation.code_is_not_6_digits);
      return;
    }
    setSubmitting(true);
    try {
      const verifyResult = await checkCode({ token: challenge, answer: code });
      if (!verifyResult.attestation) {
        setError(verifyResult.error === 'challenge_expired'
          ? L.questions.contact_confirmation.code_expired
          : L.questions.contact_confirmation.code_incorrect);
        return;
      }
      attestation.current = verifyResult.attestation;

      const info: Record<string, string> = { email };
      if (requiresBirthDate) info.birth_date = birthDate;

      const candidateResult = await getApplicantCandidates({ info, mode: 'login' });
      if (candidateResult.error || !candidateResult.portalToken) {
        setError(L.something_went_wrong);
        return;
      }
      setCookie('portal', candidateResult.portalToken, { path: '/' });
      trySetPortalToken(candidateResult.portalToken);
      history.push('/o/home');
    } finally {
      setSubmitting(false);
    }
  };

  const languageDropdown = <LanguageDropdown languages={(configuration.languages || 'en,es').split(',')} />;

  return (
    <div className="max-w-4xl mx-auto applicant-led pb-36">
      <PortalHeader leftSlot={languageDropdown} />
      <main className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-[19rem] mx-auto">
          <h1 className="text-2xl font-semibold mb-2 text-center">{L.auth.get_started}</h1>
          {descriptionMarkdown && step === 'fields' && (
            <div className={`mb-6 text-sm text-gray-700${isUsingDefaultDescription ? ' text-center' : ''}`}>{renderedDescription}</div>
          )}
          {step === 'fields' && (
            <form className="flex flex-col gap-6" onSubmit={onSubmitFields} noValidate>
              {requiresBirthDate && (
                <MonthDayYearInput
                  label={L.questions.household.birth_date}
                  required
                  value={birthDate}
                  onChange={setBirthDate}
                  legendClassName="text-sm/6 font-medium m-0 text-[var(--ak-color-typography-text)]"
                  subLabelLayout="stacked"
                  errorVariant="inline"
                />
              )}
              <Input
                label={L.auth.email_address}
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={emailError ? { hasError: true, errorMessage: emailError } : undefined}
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button
                type="submit"
                loading={submitting}
                isDisabled={!email || !!emailError || (requiresBirthDate && !birthDate)}
              >{L.auth.send_code_to_email}</Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => history.push('/o/home')}
              >
                {L.auth.back}
              </Button>
            </form>
          )}
          {step === 'code' && (
            <form className="flex flex-col gap-6" onSubmit={onSubmitCode} noValidate>
              <p className="m-0 text-sm text-gray-700">{L.auth.enter_code_sent_to_email.replace('$email', email)}</p>
              <div className="w-full [&_fieldset]:max-w-full [&_fieldset]:w-full [&_fieldset>div>div]:w-full">
                <InputOTP
                  maxLength={6}
                  autoFocus
                  label={L.auth.verification_code}
                  value={code}
                  onChange={setCode}
                >
                  <InputOTPGroup className="flex-1">
                    <InputOTPSlot index={0} className="flex-1 w-auto h-10" />
                    <InputOTPSlot index={1} className="flex-1 w-auto h-10" />
                    <InputOTPSlot index={2} className="flex-1 w-auto h-10" />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup className="flex-1">
                    <InputOTPSlot index={3} className="flex-1 w-auto h-10" />
                    <InputOTPSlot index={4} className="flex-1 w-auto h-10" />
                    <InputOTPSlot index={5} className="flex-1 w-auto h-10" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <div className="text-xs text-gray-600">{troubleshootHelper}</div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button
                type="submit"
                loading={submitting}
                isDisabled={code.length !== 6}
              >{L.apply.continue}</Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => history.replace(location.pathname)}
              >
                {L.auth.back}
              </Button>
            </form>
          )}
          {disclaimerMarkdown && (
            <div className={`mt-8 text-xs text-gray-600${isUsingDefaultDisclaimer ? ' text-center' : ''}`}>{renderedDisclaimer}</div>
          )}
        </div>
      </main>
    </div>
  );
}
